/**
 * Federated-vs-native discriminator for the CALLER'S OWN signed-in session
 * (#566). Deliberately mirrors appcore's server-side one
 * (`services/auth-triggers/src/federated-identity.ts`) so the SPA and the
 * Cognito triggers can never disagree about what a token is.
 *
 * Why the SPA needs to know: the two provenances have two different first-run
 * paths and only one of them can succeed per user. `POST /me/provision`
 * requires the ID token to carry an `identities` claim — a hub-brokering
 * defence, appcore `packages/core/src/auth/hub-link-proof-verifier.ts` — so a
 * natively issued token presented there is refused `NOT_A_FEDERATED_PROOF`,
 * always, no matter how many times it is retried.
 *
 * This is a UI-routing signal ONLY. Nothing here authorizes anything: the
 * claims are read from the local (unverified) token copy, and every endpoint
 * re-verifies provenance server-side against the signature. The worst a wrong
 * answer here can do is show the wrong explanation.
 */

/** `identities` is the AUTHORITATIVE signal: only a genuine federated sign-in
 *  produces one. Cognito puts it on the ID token as an array; some paths
 *  stringify it, so accept both (same tolerance as appcore's `parseIdentities`). */
function hasIdentities(raw: unknown): boolean {
  let value = raw;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '[]') return false;
    try {
      value = JSON.parse(trimmed);
    } catch {
      return false;
    }
  }
  return Array.isArray(value) && value.length > 0;
}

/**
 * Fallback for the (unexpected) case where `identities` is absent from the
 * claims: Cognito names a federated user `<Provider>_<providerSub>`, e.g.
 * `Google_1130…`. The invariant it relies on is the hub pool's
 * `signInAliases: { email: true }`, which makes a native username a UUID (no
 * `_`). Misfiring on an odd native username is survivable in THIS direction:
 * it routes the user to `ProvisionGate`, which now explains the
 * `NOT_A_FEDERATED_PROOF` refusal truthfully rather than saying "try again".
 */
const FEDERATED_USERNAME_PREFIX = /^[A-Za-z][A-Za-z0-9.]*_[A-Za-z0-9]/;

export type SessionProvenance = 'federated' | 'native' | 'unknown';

/**
 * `unknown` means "no token claims to read yet", never "native" — the two must
 * stay distinct, because the caller routes `unknown` toward provisioning (the
 * server is the authority) and only a POSITIVE native reading into the
 * native-only dead end.
 */
export function sessionProvenanceFromClaims(claims: Record<string, unknown> | null | undefined): SessionProvenance {
  if (!claims) return 'unknown';
  if (hasIdentities(claims['identities'])) return 'federated';
  const username = claims['cognito:username'];
  if (typeof username === 'string' && FEDERATED_USERNAME_PREFIX.test(username)) return 'federated';
  return 'native';
}
